<script>
/* eslint no-console: 0 */
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Modal from '../../../../components/Modal.vue';
// [PROSPEK] 2026-06-11 media quick replies - manage reusable media from canned response settings.
import CannedResponseAttachments from 'dashboard/components-next/CannedResponses/CannedResponseAttachments.vue';
// [/PROSPEK]

export default {
  components: {
    NextButton,
    Modal,
    WootMessageEditor,
    // [PROSPEK] 2026-06-11 media quick replies - manage reusable media from canned response settings.
    CannedResponseAttachments,
    // [/PROSPEK]
  },
  props: {
    id: { type: Number, default: null },
    edcontent: { type: String, default: '' },
    edshortCode: { type: String, default: '' },
    onClose: { type: Function, default: () => {} },
    // [PROSPEK] 2026-06-11 media quick replies - existing reusable media for this canned response.
    attachments: { type: Array, default: () => [] },
    // [/PROSPEK]
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      editCanned: {
        showAlert: false,
        showLoading: false,
      },
      shortCode: this.edshortCode,
      content: this.edcontent,
      // [PROSPEK] 2026-06-11 media quick replies - track retained and newly uploaded media for update.
      retainedAttachmentIds: this.attachments.map(attachment => attachment.id),
      uploadedBlobSignedIds: [],
      isUploadingAttachments: false,
      // [/PROSPEK]
      show: true,
    };
  },
  validations: {
    shortCode: {
      required,
      minLength: minLength(2),
    },
    content: {
      required,
    },
  },
  computed: {
    pageTitle() {
      return `${this.$t('CANNED_MGMT.EDIT.TITLE')} - ${this.edshortCode}`;
    },
  },
  methods: {
    setPageName({ name }) {
      this.v$.content.$touch();
      this.content = name;
    },
    resetForm() {
      this.shortCode = '';
      this.content = '';
      this.v$.shortCode.$reset();
      this.v$.content.$reset();
    },
    editCannedResponse() {
      // [PROSPEK] 2026-06-11 media quick replies - do not save before direct uploads finish.
      if (this.isUploadingAttachments) {
        useAlert(this.$t('CANNED_MGMT.ATTACHMENTS.UPLOADING'));
        return;
      }
      // [/PROSPEK]

      // Show loading on button
      this.editCanned.showLoading = true;
      // Make API Calls
      this.$store
        .dispatch('updateCannedResponse', {
          id: this.id,
          short_code: this.shortCode,
          content: this.content,
          // [PROSPEK] 2026-06-11 media quick replies - persist attachment retention and additions.
          retained_attachment_ids: this.retainedAttachmentIds,
          uploaded_blob_signed_ids: this.uploadedBlobSignedIds,
          // [/PROSPEK]
        })
        .then(() => {
          // Reset Form, Show success message
          this.editCanned.showLoading = false;
          useAlert(this.$t('CANNED_MGMT.EDIT.API.SUCCESS_MESSAGE'));
          this.resetForm();
          setTimeout(() => {
            this.onClose();
          }, 10);
        })
        .catch(error => {
          this.editCanned.showLoading = false;
          const errorMessage =
            error?.message || this.$t('CANNED_MGMT.EDIT.API.ERROR_MESSAGE');
          useAlert(errorMessage);
        });
    },
  },
};
</script>

<template>
  <Modal v-model:show="show" :on-close="onClose">
    <div class="flex flex-col h-auto overflow-auto">
      <woot-modal-header :header-title="pageTitle" />
      <form class="flex flex-col w-full" @submit.prevent="editCannedResponse()">
        <div class="w-full">
          <label :class="{ error: v$.shortCode.$error }">
            {{ $t('CANNED_MGMT.EDIT.FORM.SHORT_CODE.LABEL') }}
            <input
              v-model="shortCode"
              type="text"
              :placeholder="$t('CANNED_MGMT.EDIT.FORM.SHORT_CODE.PLACEHOLDER')"
              @input="v$.shortCode.$touch"
            />
          </label>
        </div>

        <div class="w-full">
          <label :class="{ error: v$.content.$error }">
            {{ $t('CANNED_MGMT.EDIT.FORM.CONTENT.LABEL') }}
          </label>
          <div class="editor-wrap">
            <WootMessageEditor
              v-model="content"
              class="message-editor [&>div]:px-1"
              :class="{ editor_warning: v$.content.$error }"
              channel-type="Context::Default"
              enable-variables
              :enable-canned-responses="false"
              :placeholder="$t('CANNED_MGMT.EDIT.FORM.CONTENT.PLACEHOLDER')"
              @blur="v$.content.$touch"
            />
          </div>
          <!-- [PROSPEK] 2026-06-11 media quick replies - manage media attachments for the canned response. -->
          <CannedResponseAttachments
            :existing-attachments="attachments"
            @update:is-uploading="isUploadingAttachments = $event"
            @update:retained-attachment-ids="retainedAttachmentIds = $event"
            @update:uploaded-blob-signed-ids="uploadedBlobSignedIds = $event"
          />
          <!-- [/PROSPEK] -->
        </div>
        <div class="flex flex-row justify-end w-full gap-2 px-0 py-2">
          <NextButton
            faded
            slate
            type="reset"
            :label="$t('CANNED_MGMT.EDIT.CANCEL_BUTTON_TEXT')"
            @click.prevent="onClose"
          />
          <NextButton
            type="submit"
            :label="$t('CANNED_MGMT.EDIT.FORM.SUBMIT')"
            :disabled="
              v$.content.$invalid ||
              v$.shortCode.$invalid ||
              isUploadingAttachments ||
              editCanned.showLoading
            "
            :is-loading="editCanned.showLoading"
          />
        </div>
      </form>
    </div>
  </Modal>
</template>

<style scoped lang="scss">
:deep(.ProseMirror-menubar) {
  @apply hidden;
}

:deep(.ProseMirror-woot-style) {
  @apply min-h-[12.5rem];
}
</style>
